import React, { Suspense } from "react";
import "./MediaListElement.css";
import "./WeekdaySchedulerEditor.css";
import "../App.css";
import path from "path";
import moment from "moment";
import {
  DeleteOutlined,
  EyeOutlined,
  PlusCircleFilled,
  CloseCircleFilled,
  EditOutlined,
  InfoCircleTwoTone,
  CheckCircleOutlined,
  ReloadOutlined,
  PlusCircleOutlined,
  RollbackOutlined,
  SyncOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Play } from "lucide-react";
import { getMediaTypeIcon } from "../helpers/mediaTypeIcons";
import { connect } from "react-redux";
import {
  requestServerPost,
  selectPage,
  requestNodeServerPost,
  requestServerGet,
} from "../actions";
import VideoThumbnail from "react-video-thumbnail"; // use npm published version

import {
  Row,
  Col,
  Button,
  Tag,
  DatePicker,
  Popover,
  Tooltip,
  Checkbox,
  Dropdown,
  Menu,
  Grid,
  Modal,
} from "antd";
import DataHelper from "../helpers/DataHelper";

import { THEME_PALLETE } from "../Theme";
import { useProLanguage, PRO_TRANSLATIONS } from "../global/ProLanguageContext";

const { RangePicker } = DatePicker;
const inputdateFormat = "YYYY-MM-DD";
const dateFormat = "DD/MM/YYYY";

const btnBase = {
  width: 30,
  height: 30,
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  background: "#fff",
  color: "#64748b",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const btnDanger = { ...btnBase, border: "1px solid #fca5a5", color: "#dc2626" };
const btnSuccess = {
  ...btnBase,
  border: "1px solid #86efac",
  color: "#16a34a",
};

function ActionsCell({
  type,
  mode,
  allowEdit,
  allowAssign,
  allowDelete,
  allowRestore,
  FileUseStatusID,
  data,
  onEditClick,
  onPreviewClick,
  onAssignClick,
  onRestoreClick,
  onReplaceClick,
  onDeleteClick,
}) {
  const screens = Grid.useBreakpoint();
  const isNarrow = !screens.xl;
  const { lang } = useProLanguage();
  const tMedia = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).media;

  const canEdit =
    (type === "web" ||
      type === "streamingurl" ||
      type === "videourl" ||
      type === "image" ||
      type === "video") &&
    allowEdit;

  if (isNarrow) {
    const menuItems = [
      {
        key: "preview",
        label: (
          <span>
            <EyeOutlined style={{ marginRight: 6 }} />
            {tMedia.actionPreview}
          </span>
        ),
        onClick: onPreviewClick,
      },
      canEdit
        ? {
            key: "edit",
            label: (
              <span>
                <EditOutlined style={{ marginRight: 6 }} />
                {tMedia.actionEdit}
              </span>
            ),
            onClick: () => onEditClick(data),
          }
        : null,
      mode !== "trash" && allowAssign
        ? {
            key: "assign",
            label: (
              <span>
                <PlusCircleOutlined style={{ marginRight: 6 }} />
                {tMedia.actionAddToPlaylists}
              </span>
            ),
            onClick: () => onAssignClick(data),
          }
        : null,
      mode === "trash" && allowRestore
        ? {
            key: "restore",
            label: (
              <span>
                <RollbackOutlined
                  style={{ marginRight: 6, color: "#16a34a" }}
                />
                <span style={{ color: "#16a34a" }}>{tMedia.actionRestore}</span>
              </span>
            ),
            onClick: onRestoreClick,
          }
        : null,
      FileUseStatusID !== 0 && mode !== "trash"
        ? {
            key: "replace",
            label: (
              <span>
                <SyncOutlined style={{ marginRight: 6 }} />
                {tMedia.actionReplaceInPlaylists}
              </span>
            ),
            onClick: onReplaceClick,
          }
        : null,
      allowDelete
        ? {
            key: "delete",
            label: (
              <span>
                <DeleteOutlined style={{ marginRight: 6, color: "#dc2626" }} />
                <span style={{ color: "#dc2626" }}>{tMedia.actionDelete}</span>
              </span>
            ),
            onClick: onDeleteClick,
          }
        : null,
    ].filter(Boolean);

    const menu = (
      <Menu items={menuItems} style={{ borderRadius: 10, minWidth: 180 }} />
    );

    return (
      <div
        style={{
          flex: "0 0 48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "0 8px",
        }}
      >
        <Dropdown
          overlay={menu}
          trigger={["click"]}
          placement="bottomRight"
        >
          <button style={{ ...btnBase, width: 32, height: 32 }}>
            <MoreOutlined style={{ fontSize: 16 }} />
          </button>
        </Dropdown>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: "0 0 190px",
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "0 12px",
        justifyContent: "flex-end",
      }}
    >
      <Tooltip title={tMedia.actionEdit}>
        {canEdit ? (
          <button style={btnBase} onClick={() => onEditClick(data)}>
            <EditOutlined style={{ fontSize: 13 }} />
          </button>
        ) : null}
      </Tooltip>
      <Tooltip title={tMedia.actionPreview}>
        <button style={btnBase} onClick={onPreviewClick}>
          <EyeOutlined style={{ fontSize: 13 }} />
        </button>
      </Tooltip>
      {mode !== "trash" && allowAssign && (
        <Tooltip title={tMedia.actionAddToPlaylists}>
          <button style={btnBase} onClick={() => onAssignClick(data)}>
            <PlusCircleOutlined style={{ fontSize: 13 }} />
          </button>
        </Tooltip>
      )}
      {mode === "trash" && allowRestore && (
        <Tooltip title={tMedia.actionRestore}>
          <button style={btnSuccess} onClick={onRestoreClick}>
            <RollbackOutlined style={{ fontSize: 13 }} />
          </button>
        </Tooltip>
      )}
      {FileUseStatusID !== 0 && mode !== "trash" && (
        <Tooltip title={tMedia.actionReplaceMedia}>
          <button style={btnBase} onClick={onReplaceClick}>
            <SyncOutlined style={{ fontSize: 13 }} />
          </button>
        </Tooltip>
      )}
      {allowDelete && (
        <Tooltip title={tMedia.actionDelete}>
          <button style={btnDanger} onClick={onDeleteClick}>
            <DeleteOutlined style={{ fontSize: 13 }} />
          </button>
        </Tooltip>
      )}
    </div>
  );
}

class MediaListElement extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      startDate: "",
      endDate: "",
      periodModalOpen: false,
      modalMoments: null,
    };
  }

  componentDidMount = () => {
    const { data = {} } = this.props;
    const { StartDate = "", EndDate = "" } = data;

    this.setState({ startDate: StartDate, endDate: EndDate });
  };

  openPeriodModal = () => {
    const { startDate, endDate } = this.state;
    const defaultMoments =
      startDate && endDate
        ? [moment(startDate, inputdateFormat), moment(endDate, inputdateFormat)]
        : [moment(), moment()];
    this.setState({ periodModalOpen: true, modalMoments: defaultMoments });
  };

  savePeriodFromModal = () => {
    const { data = {} } = this.props;
    const { modalMoments } = this.state;
    if (!modalMoments || !modalMoments[0] || !modalMoments[1]) return;
    const newStart = modalMoments[0].format(inputdateFormat);
    const newEnd = modalMoments[1].format(inputdateFormat);
    this.setState({
      startDate: newStart,
      endDate: newEnd,
      periodModalOpen: false,
    });
    this.updateMediaStartEndDate(data, newStart, newEnd);
  };

  updateMediaStartEndDate = (mediaData, startdate, enddate) => {
    const { requestServerPost, usertoken } = this.props;
    const params = new FormData();

    // console.log("mediaData " + JSON.stringify(mediaData))

    params.append("fileid", mediaData.FileID);
    params.append("startdate", startdate);
    params.append("enddate", enddate);

    requestServerPost("update_file", params, {}, (response) => {}, usertoken);
  };

  updateMediaName = (mediaData, mediaName) => {
    const { requestServerPost, usertoken } = this.props;
    const params = new FormData();

    // console.log("mediaData " + JSON.stringify(mediaData))

    params.append("fileid", mediaData.FileID);
    params.append("filename", mediaName);

    requestServerPost("update_file", params, {}, (response) => {}, usertoken);
  };

  render() {
    const {
      mode = "media",
      selectable = false,
      isSelected = false,
      data = {},
      view = "large",
      onDeleteClick = () => {},
      onRestoreClick = () => {},
      onPreviewClick = () => {},
      onReplaceClick = () => {},
      onEditClick = () => {},
      onAssignClick = () => {},
      onSelectionChanged = () => {},
      allowEdit = true,
      allowAssign = true,
      allowDelete = true,
      allowRestore = false,
      selectedBrandData,
      branches = [],
      tMedia = {},
    } = this.props;
    const {
      FileName = "",
      size = 0,
      UploadFile = "",
      FileType = "",
      FileTypeSub = "",
      AddDate = "",
      DeleteDate = "",
      FileUseStatus = "",
      FileUseStatusID = 1,
      FileAvailable = "",
      FileAvailableID = 1,
      DaysRemaining = "",
      FileID = "",
      CreateLevel,
      AccessBranches,
    } = data;

    const sharingMode = selectedBrandData
      ? selectedBrandData.ContentSharingMode
      : null;
    const showOwnership =
      (sharingMode == 2 || sharingMode == "2") && mode !== "trash";
    const showBranchesAccess =
      (sharingMode == 2 || sharingMode == "2") && mode !== "trash";

    // Build a lookup map from branch ID to branch name
    const branchMap = {};
    branches.forEach((b) => {
      if (b.BranchID)
        branchMap[b.BranchID] = b.BranchName || `Branch ${b.BranchID}`;
    });

    var icon = UploadFile;

    const startDate = this.state.startDate;
    const endDate = this.state.endDate;

    const filePropJSON = DataHelper.extractFileProperties(data);
    const showInfoIcon =
      Object.keys(filePropJSON).length > 0 &&
      "width" in filePropJSON &&
      "height" in filePropJSON;
    const renderFileProperties = (fileProp) => {
      const { width, height, duration } = fileProp;
      const resolutionString = `Resolution : ${width} X ${height}`;
      var durationString = ``;
      if ("duration" in fileProp) {
        durationString = `Duration : ${duration} seconds`;
      }
      return (
        <div>
          {[resolutionString, durationString].map((e, index) => (
            <div key={`property_${FileID}_${index}`}>{e}</div>
          ))}
        </div>
      );
    };

    const type = FileType.toLocaleLowerCase();
    var description = type;
    switch (type) {
      case "video":
        icon = getMediaTypeIcon("video", 24);
        break;
      case "streamingurl":
        icon = getMediaTypeIcon("streamingurl", 24);
        description = UploadFile;
        break;
      case "web":
        icon = getMediaTypeIcon("web", 24);
        description = UploadFile;
        break;
      case "videourl":
        icon = getMediaTypeIcon("videourl", 24);
        description = UploadFile;
        break;
      case "image":
        icon = (
          <img
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
            src={UploadFile}
            onError={(e) => {
              e.target.onerror = null; // prevent infinite loop if fallback fails
              e.target.src =
                "/cms/assets/images/placeholders/image_placeholder.png"; // fallback image
            }}
            alt="Media"
          />
        );
        break;
      default:
        icon = UploadFile;
        break;
    }

    var renderIcon =
      typeof icon === "string" ? (
        <img
          alt={icon}
          style={{ width: "45px" }}
          className="media-thumb-small"
          src={icon}
        ></img>
      ) : (
        <div
          className="center-content"
          style={{
            width: "45px",
            margin: "5px",
            height: "35px",
            fontSize: "24px",
            textAlign: "center",
            display: "inline-block",
            objectFit: "cover",
          }}
        >
          {icon}
        </div>
      );

    if (type === "video" || type == "videourl") {
      renderIcon = (
        <div
          style={{
            backgroundColor: "black",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: "20px",
            width: "45px",
            height: "80%",
            margin: "auto",
            objectFit: "contain",
          }}
        >
          <Play
            size={22}
            color="white"
            style={{ position: "absolute", zIndex: 3 }}
          />
          <div
            style={{
              position: "absolute",
              backgroundColor: "black",
              opacity: 0.2,
              width: "100%",
              height: "100%",
              zIndex: 2,
            }}
          ></div>
          {/* <LazyLoadComponent>
                        <Suspense fallback={null}>
                            <video preload={"metadata"} controls={false} className="media-thumb" style={{ width: "100%", height: "100%", zIndex: 1 }}>
                                <source src={`${UploadFile}#t=2`} type="video/mp4" />
                            </video>
                        </Suspense>
                    </LazyLoadComponent> */}
          {/* <VideoThumbnail
                        videoUrl={UploadFile}
                        snapshotAtTime={2}
                        thumbnailHandler={(thumbnail) => console.log(thumbnail)}
                        width={50}
                        height={50}
                    /> */}
        </div>
      );

      if (view !== "small") {
        renderIcon = (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "#0f172a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 8,
            }}
          >
            <Play size={22} color="#fff" />
          </div>
        );
      }
    } else {
      if (view !== "small")
        renderIcon =
          typeof icon === "string" ? (
            <img
              alt={icon}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              className="media-thumb"
              src={icon}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "26px",
              }}
            >
              {icon}
            </div>
          );
    }

    const typeColor = type === "video" ? "#f50" : "#108ee9";
    var content = null;
    switch (view) {
      case "small":
        content = (
          <div className="media-list-element-small">
            {/* Thumbnail */}
            <div
              onClick={onPreviewClick}
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                flexShrink: 0,
                overflow: "hidden",
                cursor: "pointer",
                background: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {renderIcon}
            </div>
            {/* Text */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                paddingLeft: 10,
                paddingRight: 8,
              }}
            >
              <Tooltip title={FileName}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: "#1e293b",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: "1.3",
                  }}
                >
                  {FileName}
                </div>
              </Tooltip>
              <div
                style={{
                  fontSize: 11,
                  color: "#94a3b8",
                  marginTop: 2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: "1.3",
                }}
              >
                {description}
              </div>
            </div>
            {/* Type pill */}
            <div style={{ flexShrink: 0, paddingRight: 10 }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 20,
                  background:
                    type === "image"
                      ? "#eff6ff"
                      : type === "video"
                        ? "#fee2e2"
                        : type === "web"
                          ? "#f0fdf4"
                          : "#f5f3ff",
                  color:
                    type === "image"
                      ? "#2563eb"
                      : type === "video"
                        ? "#dc2626"
                        : type === "web"
                          ? "#16a34a"
                          : "#7c3aed",
                  border: `1px solid ${type === "image" ? "#bfdbfe" : type === "video" ? "#fca5a5" : type === "web" ? "#86efac" : "#c4b5fd"}`,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {FileType}
              </span>
            </div>
          </div>
        );
        break;
      default:
        content = (
          <div
            className="media-list-element"
            style={
              isSelected
                ? { borderColor: "#6366f1", backgroundColor: "#eef2ff" }
                : {}
            }
          >
            {/* Left accent bar */}
            <div
              style={{
                width: 4,
                flexShrink: 0,
                alignSelf: "stretch",
                borderRadius: "4px 0 0 4px",
                background:
                  type === "image"
                    ? "#ec4899"
                    : type === "video" || type === "videourl"
                      ? "#6366f1"
                      : type === "web"
                        ? "#14b8a6"
                        : type === "streamingurl"
                          ? "#f59e0b"
                          : "#6366f1",
              }}
            />
            {/* Checkbox */}
            {selectable && (
              <div
                className="media-checkbox-col"
                style={{
                  flex: "0 0 100px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Checkbox checked={isSelected} onChange={onSelectionChanged} />
              </div>
            )}
            {/* Thumbnail */}
            <div
              className="media-thumb-box"
              onClick={onPreviewClick}
              style={{
                width: 52,
                height: 52,
                borderRadius: 10,
                flexShrink: 0,
                overflow: "hidden",
                cursor: "pointer",
                background: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 10px 0 8px",
              }}
            >
              {renderIcon}
            </div>
            {/* Main info — matches header's flex:"1 1 260px" so it takes
                the leftover space while the meta columns stay a fixed
                group anchored to the right */}
            <div
              style={{
                flex: "1 1 260px",
                minWidth: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                {showInfoIcon && (
                  <Popover content={renderFileProperties(filePropJSON)}>
                    <InfoCircleTwoTone
                      style={{
                        cursor: "pointer",
                        fontSize: 13,
                        flexShrink: 0,
                      }}
                    />
                  </Popover>
                )}
                <Popover
                  content={
                    <div style={{ maxWidth: 400, wordWrap: "break-word" }}>
                      {FileName}
                    </div>
                  }
                  trigger="hover"
                  placement="topLeft"
                >
                  <div
                    className="media-filename-text"
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#0f172a",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "100%",
                    }}
                  >
                    {FileName}
                  </div>
                </Popover>
              </div>
              <div
                className="media-file-date-text"
                style={{
                  fontSize: 11,
                  color: "#94a3b8",
                  marginTop: 2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {description !== type ? description : AddDate}
              </div>
              {/* Mobile-only compact info row */}
              {mode !== "trash" && (
                <div
                  className="media-info-mobile"
                  style={{
                    alignItems: "center",
                    gap: 4,
                    marginTop: 4,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    className="media-status-badge"
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: "1px 8px",
                      borderRadius: 20,
                      whiteSpace: "nowrap",
                      background: FileUseStatusID == 1 ? "#eff6ff" : "#fff7ed",
                      color: FileUseStatusID == 1 ? "#2563eb" : "#ea580c",
                      border: `1px solid ${FileUseStatusID == 1 ? "#bfdbfe" : "#fed7aa"}`,
                    }}
                  >
                    {FileUseStatus.toUpperCase()}
                  </span>
                  <span
                    className="media-status-badge"
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: "1px 8px",
                      borderRadius: 20,
                      whiteSpace: "nowrap",
                      background: FileAvailableID == 0 ? "#fef2f2" : "#f0fdf4",
                      color: FileAvailableID == 0 ? "#dc2626" : "#16a34a",
                      border: `1px solid ${FileAvailableID == 0 ? "#fecaca" : "#bbf7d0"}`,
                    }}
                  >
                    {FileAvailable.toUpperCase()}
                  </span>
                  {startDate !== "" && endDate !== "" ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <div
                        className="media-period-text-mobile"
                        onClick={this.openPeriodModal}
                        style={{
                          cursor: "pointer",
                          fontSize: "10px",
                          color: "#475569",
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <span>
                          {moment(startDate, inputdateFormat).format(
                            dateFormat,
                          )}
                        </span>
                        <span>-</span>
                        <span>
                          {moment(endDate, inputdateFormat).format(dateFormat)}
                        </span>
                      </div>
                      <div
                        onClick={(e) => {
                          this.setState({ startDate: "", endDate: "" });
                          this.updateMediaStartEndDate(data, "", "");
                          e.preventDefault();
                        }}
                        style={{
                          cursor: "pointer",
                          color: "#cbd5e1",
                          lineHeight: 1,
                        }}
                      >
                        <CloseCircleFilled
                          style={{ fontSize: 14, color: "#ddd" }}
                        />
                      </div>
                    </div>
                  ) : (
                    <button
                      className="media-add-period-mobile"
                      onClick={this.openPeriodModal}
                      style={{
                        background: "none",
                        border: "1px dashed #c7d2fe",
                        borderRadius: 6,
                        color: "#6366f1",
                        fontSize: 10,
                        fontWeight: 500,
                        padding: "1px 7px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <PlusCircleFilled style={{ fontSize: 10 }} /> Add Period
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* Meta columns — fixed-width group anchored to the right,
                matching the header's grouping */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
            {/* Type pill */}
            <div
              className="media-col-desktop"
              style={{
                flex: "0 0 120px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 20,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  display: "inline-block",
                  minWidth: 62,
                  textAlign: "center",
                  background:
                    type === "image"
                      ? "#fdf2f8"
                      : type === "video" || type === "videourl"
                        ? "#eef2ff"
                        : type === "web"
                          ? "#f0fdfa"
                          : "#fffbeb",
                  color:
                    type === "image"
                      ? "#be185d"
                      : type === "video" || type === "videourl"
                        ? "#4338ca"
                        : type === "web"
                          ? "#0f766e"
                          : "#b45309",
                  border: `1px solid ${type === "image" ? "#fbcfe8" : type === "video" || type === "videourl" ? "#c7d2fe" : type === "web" ? "#99f6e4" : "#fde68a"}`,
                }}
              >
                {FileType}
                {FileTypeSub ? ` (${FileTypeSub})` : ""}
              </span>
            </div>
            {/* Active period */}
            {mode !== "trash" && (
              <div
                className="media-col-desktop"
                style={{
                  flex: "0 0 240px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {startDate == "" && endDate == "" ? (
                  <button
                    onClick={this.openPeriodModal}
                    style={{
                      background: "none",
                      border: "1px dashed #c7d2fe",
                      borderRadius: 8,
                      color: "#6366f1",
                      fontSize: 11,
                      fontWeight: 500,
                      padding: "3px 10px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <PlusCircleFilled style={{ fontSize: 12 }} />{" "}
                    {tMedia.addPeriod || "Add Period"}
                  </button>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      justifyContent: "center",
                    }}
                  >
                    <div
                      onClick={this.openPeriodModal}
                      style={{
                        cursor: "pointer",
                        fontSize: "11px",
                        color: "#475569",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <span>
                        {moment(startDate, inputdateFormat).format(dateFormat)}
                      </span>
                      <span>-</span>
                      <span>
                        {moment(endDate, inputdateFormat).format(dateFormat)}
                      </span>
                    </div>
                    <div
                      onClick={(e) => {
                        this.setState({ startDate: "", endDate: "" });
                        this.updateMediaStartEndDate(data, "", "");
                        e.preventDefault();
                      }}
                      style={{ cursor: "pointer", lineHeight: 1 }}
                    >
                      <CloseCircleFilled
                        style={{
                          color: "#cbd5e1",
                          fontSize: 14,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Ownership */}
            {showOwnership && (
              <div
                className="media-col-desktop"
                style={{
                  flex: "0 0 80px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Tag
                  color={CreateLevel === 1 ? "gold" : "blue"}
                  style={{ margin: 0 }}
                >
                  {CreateLevel === 1
                    ? tMedia.ownershipWorkspace
                    : tMedia.ownershipLocation}
                </Tag>
              </div>
            )}
            {/* Branch access */}
            {showBranchesAccess &&
              (() => {
                const VISIBLE = 1;
                const isAll =
                  !AccessBranches ||
                  AccessBranches === "null" ||
                  AccessBranches === "all" ||
                  AccessBranches === "";
                const branchIds = isAll
                  ? []
                  : String(AccessBranches)
                      .split(",")
                      .map((s) => s.trim())
                      .filter((id) => id !== "");
                const visible = branchIds.slice(0, VISIBLE);
                const overflow = branchIds.slice(VISIBLE);
                return (
                  <div
                    className="media-col-desktop"
                    style={{
                      flex: "0 0 140px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isAll ? (
                      <Tag color="green" style={{ margin: 0 }}>
                        {tMedia.filterAllBranches}
                      </Tag>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          justifyContent: "center",
                          flexWrap: "nowrap",
                          pointerEvents: "none",
                        }}
                      >
                        {visible.map((id) => (
                          <Tag key={id} color="blue" style={{ margin: 0 }}>
                            {branchMap[id] || `Branch ${id}`}
                          </Tag>
                        ))}
                        {overflow.length > 0 && (
                          <Tooltip
                            title={
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "4px",
                                  padding: "2px 0",
                                }}
                              >
                                {overflow.map((id) => (
                                  <Tag
                                    key={id}
                                    color="blue"
                                    style={{ margin: 0 }}
                                  >
                                    {branchMap[id] || `Branch ${id}`}
                                  </Tag>
                                ))}
                              </div>
                            }
                            color="#1a1a2e"
                          >
                            <Tag
                              color="blue"
                              style={{
                                margin: 0,
                                cursor: "pointer",
                                pointerEvents: "auto",
                              }}
                            >
                              +{overflow.length}
                            </Tag>
                          </Tooltip>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            {/* Trash columns */}
            {mode == "trash" && (
              <div
                style={{
                  flex: "0 0 160px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                className="media-name media-col-desktop"
              >{`${DeleteDate} `}</div>
            )}
            {mode == "trash" && (
              <div
                style={{
                  flex: "0 0 160px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                className="media-name media-col-desktop"
              >{`${DaysRemaining} ${tMedia.labelDays}`}</div>
            )}
            {/* Usage + Expiry */}
            {mode !== "trash" && (
              <>
                <div
                  className="media-col-desktop"
                  style={{
                    flex: "0 0 88px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "2px 10px",
                      borderRadius: 20,
                      whiteSpace: "nowrap",
                      display: "inline-block",
                      minWidth: 72,
                      textAlign: "center",
                      background: FileUseStatusID == 1 ? "#eff6ff" : "#fff7ed",
                      color: FileUseStatusID == 1 ? "#2563eb" : "#ea580c",
                      border: `1px solid ${FileUseStatusID == 1 ? "#bfdbfe" : "#fed7aa"}`,
                    }}
                  >
                    {FileUseStatusID == 1
                      ? tMedia.filterInUse || FileUseStatus.toLocaleUpperCase()
                      : tMedia.filterNotInUse ||
                        FileUseStatus.toLocaleUpperCase()}
                  </span>
                </div>
                <div
                  className="media-col-desktop"
                  style={{
                    flex: "0 0 80px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "2px 10px",
                      borderRadius: 20,
                      whiteSpace: "nowrap",
                      display: "inline-block",
                      minWidth: 64,
                      textAlign: "center",
                      background: FileAvailableID == 0 ? "#fef2f2" : "#f0fdf4",
                      color: FileAvailableID == 0 ? "#dc2626" : "#16a34a",
                      border: `1px solid ${FileAvailableID == 0 ? "#fecaca" : "#bbf7d0"}`,
                    }}
                  >
                    {FileAvailableID == 0
                      ? tMedia.filterExpired || "EXPIRED"
                      : tMedia.filterActive || "ACTIVE"}
                  </span>
                </div>
              </>
            )}
            </div>
            {/* Action buttons */}
            <ActionsCell
              type={type}
              mode={mode}
              allowEdit={allowEdit}
              allowAssign={allowAssign}
              allowDelete={allowDelete}
              allowRestore={allowRestore}
              FileUseStatusID={FileUseStatusID}
              data={data}
              onEditClick={onEditClick}
              onPreviewClick={onPreviewClick}
              onAssignClick={onAssignClick}
              onRestoreClick={onRestoreClick}
              onReplaceClick={onReplaceClick}
              onDeleteClick={onDeleteClick}
            />
          </div>
        );
        break;
    }
    const periodModal = (
      <Modal
        open={this.state.periodModalOpen}
        title={tMedia.confirmChangePeriodTitle || "Set Active Period"}
        onOk={this.savePeriodFromModal}
        onCancel={() => this.setState({ periodModalOpen: false })}
        okText={tMedia.confirmChangePeriodOk || "Confirm"}
        cancelText={tMedia.confirmChangePeriodCancel || "Cancel"}
        centered
      >
        <div style={{ padding: "8px 0" }}>
          <RangePicker
            value={this.state.modalMoments}
            onChange={(moments) => this.setState({ modalMoments: moments })}
            format={dateFormat}
            style={{ width: "100%" }}
          />
        </div>
      </Modal>
    );
    return (
      <>
        {periodModal}
        {content}
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    userdata: state.userdata,
    usertoken: state.usertoken,
    selectedBrandData: state.selectedBrandData,
    selectedBranchData: state.selectedBranchData,
  };
};

const ConnectedMediaListElement = connect(mapStateToProps, {
  requestServerGet,
  requestServerPost,
  selectPage,
  requestNodeServerPost,
})(MediaListElement);

const MediaListElementWithLang = (props) => {
  const { lang } = useProLanguage();
  const tMedia = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).media;
  return <ConnectedMediaListElement {...props} tMedia={tMedia} />;
};

export default MediaListElementWithLang;
